import { calculateMarketPricesRange, nameize } from '@ltvco/refresh-lib/utils';
import {
  PhoneReport,
  PersonReport,
  ReportFactory,
  VehicleReport,
} from '@ltvco/refresh-lib/v1';

import { AVM_PARTNERS_ARRAY } from 'components/ChatBV/constants';
import {
  ProcessedPropertyReportData,
  ProcessedEmailReportData,
  ProcessedPhoneReportData,
  ProcessedVehicleReportData,
  GenericReportResponse,
} from 'components/ChatBV/types';

const convertBooleanToYesNo = (value: any, fallback: string = 'no'): string => {
  if (typeof value === 'boolean') {
    return value ? 'yes' : 'no';
  }
  if (typeof value === 'string') {
    return value;
  }
  return fallback;
};

export const processPropertyReportForOverviewAndOwners = (
  coreResult: any
): ProcessedPropertyReportData | null => {
  let data = null;

  if (coreResult?.data) {
    data = coreResult.data;
  } else if (coreResult?.data?.data) {
    data = coreResult.data.data;
  } else if (coreResult?.entities || coreResult?.address) {
    data = coreResult;
  }

  if (!data) {
    return null;
  }

  const property = data.entities?.properties?.[0];

  if (!property) {
    return null;
  }

  const marketRange = calculateMarketPricesRange(
    property?.values?.market ?? [],
    AVM_PARTNERS_ARRAY
  );

  const result = {
    data: {
      parcel_address: {
        full: property.parcel_address?.full,
        parsed: {
          city: property.parcel_address?.parsed?.city,
          state: property.parcel_address?.parsed?.state,
          zip5: property.parcel_address?.parsed?.zip5,
        },
      },
      owners: property.owners
        ? property.owners.splice(0, 2).map((owner: any) => ({
            name: owner?.names?.[0]
              ? {
                  parsed: {
                    first: owner.names[0]?.parsed?.first,
                    last: owner.names[0]?.parsed?.last,
                  },
                }
              : {},
          }))
        : [],
      yearBuilt: property?.buildings?.[0]?.year_built || '',
      squareFootage: property?.buildings?.[0]?.area || '',
      dimensions: property?.dimensions?.size || '',
      bedrooms: property?.buildings?.[0]?.rooms?.bed || '',
      bathrooms: property?.buildings?.[0]?.rooms?.baths?.total || '',
      propertyType: property?.legal?.land_use?.standardized_land_use_code || '',
      estimatedValue: marketRange?.maxRange
        ? String(marketRange?.maxRange)
        : marketRange?.minRange
        ? String(marketRange?.minRange)
        : '',
      propertyTax: property?.taxes?.bills?.[0]?.amount || '',
      propertyTaxYear: property?.taxes?.bills?.[0]?.year || '',
    },
  };

  return result;
};

export const processEmailReportForOverviewAndOwners = (
  coreResult: any
): ProcessedEmailReportData | null => {
  let data = null;

  if (coreResult?.data) {
    data = coreResult.data;
  } else if (coreResult?.data?.data) {
    data = coreResult.data.data;
  } else if (coreResult?.entities || coreResult?.address) {
    data = coreResult;
  }

  if (!data) {
    return null;
  }

  const owner = data.entities?.people?.[0];

  if (!owner) {
    return null;
  }

  const result = {
    data: {
      ownerName: nameize(owner?.identity?.names?.[0]?.full || ''),
      emails:
        owner?.contact?.emails
          ?.slice(0, 2)
          ?.map((email: { address: string }) => email.address) || [],
      phones:
        owner?.contact?.phones
          ?.slice(0, 2)
          ?.map((phone: { number: string }) => phone.number) || [],
      usernames:
        owner?.social?.usernames
          ?.slice(0, 2)
          ?.map((username: { username: string }) => username.username) || [],
      addresses:
        owner?.contact?.addresses
          ?.slice(0, 2)
          ?.map((address: { full: string }) => address.full) || [],
    },
  };

  return result;
};
//vehicle phone
export const processPhoneReportForOverviewAndOwners = (
  data: GenericReportResponse | null
): ProcessedPhoneReportData | null => {
  let report: PhoneReport | null;
  const coreResult = data as any;

  if (coreResult?.entities) {
    report = ReportFactory.create(coreResult as any, 'phone');
  } else {
    report = ReportFactory.create(coreResult?.data as any, 'phone');
  }

  if (!report) {
    return null;
  }

  const phoneNumber = report.phone;
  const owner = report.getOwner({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: 50,
  });
  const { ownerName } = owner;

  const serviceProvider = report.data.meta?.search_data?.carrier;

  const risk = report.supportingPhone?.risk;
  const connectivity = report.supportingPhone?.connectivity;
  const business = report.data?.rawData?.entities?.businesses?.[0];
  const businessName = business?.names?.[0]?.name || '';

  const originalData = coreResult as any;
  const supportingPhone = originalData?.supporting?.phones?.[0];
  const person = originalData?.entities?.people?.[0];

  const result = {
    data: {
      owner_fn: ownerName,

      isRisky: convertBooleanToYesNo(risk?.is_risky),
      isRecentAbuse: convertBooleanToYesNo(risk?.is_recent_abuse),
      isActive: convertBooleanToYesNo(connectivity?.is_active),

      numSearches:
        typeof report.data?.num_searches === 'string'
          ? report.data.num_searches
          : String(report.data?.num_searches || ''),
      searchCount: String(report.data?.num_searches || 0),
      businessName,
      phoneNumber,

      serviceProvider:
        typeof serviceProvider === 'string'
          ? serviceProvider
          : String(serviceProvider || ''),
      location: (() => {
        const city =
          supportingPhone?.location?.address?.parsed?.city ||
          person?.contact?.addresses?.[0]?.parsed?.city ||
          '';
        const state =
          supportingPhone?.location?.address?.parsed?.state ||
          person?.contact?.addresses?.[0]?.parsed?.state ||
          '';
        const zipCode =
          supportingPhone?.location?.address?.parsed?.zip5 ||
          person?.contact?.addresses?.[0]?.parsed?.zip5 ||
          '';
        const country =
          supportingPhone?.location?.address?.parsed?.country ||
          person?.contact?.addresses?.[0]?.parsed?.country ||
          '';

        const parts = [city, state, zipCode, country].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : '';
      })(),

      zipCode:
        typeof supportingPhone?.location?.address?.parsed?.zip5 === 'string'
          ? supportingPhone?.location?.address?.parsed?.zip5
          : typeof person?.contact?.addresses?.[0]?.parsed?.zip5 === 'string'
          ? person?.contact?.addresses?.[0]?.parsed?.zip5
          : '',

      timezone:
        typeof supportingPhone?.location?.timezone === 'string'
          ? supportingPhone?.location?.timezone
          : '',
      lineType:
        typeof supportingPhone?.connectivity?.line_type === 'string'
          ? supportingPhone?.connectivity?.line_type
          : '',

      isLeaked: convertBooleanToYesNo(supportingPhone?.risk?.is_leaked),
      isSpammer: convertBooleanToYesNo(supportingPhone?.risk?.is_spammer),
      associatedNames:
        typeof supportingPhone?.associated?.names === 'object'
          ? JSON.stringify(
              supportingPhone?.associated?.names?.map((n: any) => n.full)
            )
          : '',
      associatedEmails:
        typeof supportingPhone?.associated?.emails === 'object'
          ? JSON.stringify(
              supportingPhone?.associated?.emails?.map((e: any) => e.address)
            )
          : '',
    },
  };
  return result;
};

export const processVehicleReportForOverviewAndOwners = (
  data: GenericReportResponse | null
): ProcessedVehicleReportData | null => {
  if (!data) {
    return null;
  }
  let year = '';
  let make = '';
  let model = '';

  const newReport = new VehicleReport(data as any);
  const reportData = newReport.data;

  if (reportData.automobiles) {
    const newYear = reportData.automobiles[0]?.year?.toString() || '';
    const newMake = reportData.automobiles[0]?.make || '';
    const newModel = reportData.automobiles[0]?.model || '';

    if (newYear !== year) {
      year = newYear;
    }
    if (newMake !== make) {
      make = newMake;
    }
    if (newModel !== model) {
      model = newModel;
    }
  }

  const result = {
    data: {
      vin: reportData.automobiles[0]?.vin || '',
      owner:
        typeof reportData.automobiles[0]?.owner === 'string'
          ? reportData.automobiles[0]?.owner
          : JSON.stringify(reportData.automobiles[0]?.owner || ''),

      vehicle_type:
        typeof reportData.automobiles[0]?.vehicle_type === 'string'
          ? reportData.automobiles[0]?.vehicle_type
          : JSON.stringify(reportData.automobiles[0]?.vehicle_type || ''),

      color:
        typeof reportData.automobiles[0]?.color === 'string'
          ? reportData.automobiles[0]?.color
          : typeof reportData.automobiles[0]?.design?.current_color === 'string'
          ? reportData.automobiles[0]?.design?.current_color
          : JSON.stringify(reportData.automobiles[0]?.color || ''),
      trim:
        typeof reportData.automobiles[0]?.trim === 'string'
          ? reportData.automobiles[0]?.trim
          : typeof reportData.automobiles[0]?.design?.trim === 'string'
          ? reportData.automobiles[0]?.design?.trim
          : typeof reportData.automobiles[0]?.design?.short_trim === 'string'
          ? reportData.automobiles[0]?.design?.short_trim
          : JSON.stringify(reportData.automobiles[0]?.trim || ''),
      description:
        reportData.automobiles[0]?.vehicleYMM ||
        `${year} ${make} ${model}`.trim() ||
        '',
      location: (() => {
        const state = reportData.automobiles[0]?.state || '';
        const city =
          reportData.automobiles[0]?.city ||
          reportData.automobiles[0]?.origin?.city ||
          '';
        const zip = reportData.automobiles[0]?.zip || '';
        const county = reportData.automobiles[0]?.county || '';
        const country = reportData.automobiles[0]?.origin?.country || '';

        const parts = [city, state, zip, county, country].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : '';
      })(),
      year,
      make,
      model,
      size:
        typeof reportData.automobiles[0]?.specs?.size === 'string'
          ? reportData.automobiles[0]?.specs?.size
          : String(reportData.automobiles[0]?.specs?.size || ''),
      invoicePrice:
        typeof reportData.automobiles[0]?.prices?.invoice_price === 'string'
          ? reportData.automobiles[0]?.prices?.invoice_price
          : String(reportData.automobiles[0]?.prices?.invoice_price || ''),
      bodyStyle:
        typeof reportData.automobiles[0]?.body_style === 'string'
          ? reportData.automobiles[0]?.body_style
          : String(reportData.automobiles[0]?.body_style || ''),
      type:
        typeof reportData.automobiles[0]?.type === 'string'
          ? reportData.automobiles[0]?.type
          : String(reportData.automobiles[0]?.type || ''),
      estimatedMileage:
        typeof reportData.automobiles[0]?.estimated_mileage?.miles === 'string'
          ? reportData.automobiles[0]?.estimated_mileage?.miles
          : String(reportData.automobiles[0]?.estimated_mileage?.miles || ''),
    },
  };

  return result;
};

export const processPersonReportForOverviewAndOwners = (
  data: GenericReportResponse | null
) => {
  let report: PersonReport | null;
  const coreResult = data as any;

  if (coreResult?.entities) {
    report = ReportFactory.create(coreResult as any, 'person');
  } else {
    report = ReportFactory.create(coreResult?.data as any, 'person');
  }

  if (!report) {
    return null;
  }

  const person = report.getOwner({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: 50,
  });
  const { ownerName } = person;

  return {
    data: {
      personName: ownerName,
      age: person.identity?.ages?.[0]?.years || '',
      dateOfBirth: person.identity?.dobs?.[0]?.date || '',
      location: person.location
        ? `${person.location?.parsed?.city}, ${person.location?.parsed?.state}`
        : '',
      aliases: person.identity?.names?.map((name) => nameize(name.full)) || [],
      phone: person?.phones?.[0]?.formattedPhone || '',
      email: person?.emails?.[0]?.address || '',
      address: person?.addresses?.[0]?.full || '',
      relatives: person?.relatives?.map((relative) => relative.name) || [],
      jobs: person?.jobs?.[0]?.title || person?.jobs?.[0]?.company || '',
      education: person?.educations[0]?.school || '',
    },
  };
};
